import { useUpdateEffect } from "ahooks";
import { duration } from "common";
import { groupBy } from "lodash";
import { useMemo } from "react";
import { DeliveryReport, Message } from "types";
import { useSeller, useUser } from "@/hooks/useAccount";
import { Options, useCollection } from "@/hooks/useCollection";
import { isDeliveryInFlight } from "@/models/DeliveryReport";
import { isOutboundSms } from "@/models/Message";

// The indicator is supplementary: a denied or failed lookup hides it rather than flashing an error on the timeline.
const apiConfig = { suppressErrorToast: true };

/** SMS usually settles within seconds, so poll briefly while a report is still in flight. */
const IN_FLIGHT_POLL_INTERVAL = duration(10, "seconds");

const getRefetchInterval = (reports: DeliveryReport[] = []) => {
    return reports.some((report) => isDeliveryInFlight(report)) ? IN_FLIGHT_POLL_INTERVAL : false;
};

/**
 * One observer per conversation, since react-query schedules a poll timer per observer.
 * One page only: a conversation past the page size loses indicators on its oldest messages.
 */
export const useMessageDeliveryReports = (conversationId: ID | undefined, messages: Message[]) => {
    const seller = useSeller();
    const user = useUser();
    const outboundSmsIds = messages
        .filter((message) => isOutboundSms({ message, seller, user }))
        .map(({ id }) => id)
        .join(",");
    const isEnabled = !!outboundSmsIds && !!conversationId;

    const options: Options<DeliveryReport> = {
        apiConfig,
        refetchOnMount: "always",
        notifyOnChangeProps: ["data"],
        refetchInterval: getRefetchInterval,
        enabled: isEnabled,
    };
    const { data, refetch } = useCollection<DeliveryReport>(
        "deliveryReports",
        { conversation: conversationId },
        options,
    );

    // Polling stops once earlier reports settle, so a newly sent text has to trigger its own fetch.
    useUpdateEffect(() => {
        if (isEnabled) {
            refetch();
        }
    }, [outboundSmsIds]);

    const reportsByMessage = useMemo(() => groupBy(data, "message"), [data]);

    return { reportsByMessage };
};
